/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as PremiumModalActionCreators from '@app/actions/PremiumModalActionCreators';
import type {SelectOption} from '@app/components/form/Select';
import {Select} from '@app/components/form/Select';
import styles from '@app/components/modals/tabs/components/EntranceSoundSection.module.css';
import {useEntranceSound} from '@app/components/modals/tabs/hooks/useEntranceSound';
import {Button} from '@app/components/uikit/button/Button';
import {Tooltip} from '@app/components/uikit/tooltip/Tooltip';
import GuildStore from '@app/stores/GuildStore';
import SelectedGuildStore from '@app/stores/SelectedGuildStore';
import * as AvatarUtils from '@app/utils/AvatarUtils';
import {
	DMS_ENTRANCE_SOUND_SCOPE,
	GLOBAL_ENTRANCE_SOUND_SCOPE,
	GUILDS_ENTRANCE_SOUND_SCOPE,
	getEntranceSoundScopeId,
	parseEntranceSoundScopeId,
	type EntranceSoundScope,
} from '@app/utils/EntranceSoundScopes';
import {LimitResolver} from '@app/utils/limits/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/utils/limits/LimitUtils';
import {shouldShowPremiumFeatures} from '@app/utils/PremiumUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon, SpeakerHighIcon, TrashIcon, UploadIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {clsx} from 'clsx';
import {useCallback, useMemo, useState} from 'react';

interface EntranceSoundScopeOption extends SelectOption<string> {
	value: string;
	label: string;
	description: string;
	icon?: string | null;
}

export const EntranceSoundSection: React.FC = observer(() => {
	const hasVoiceEntranceSounds = useMemo(
		() =>
			isLimitToggleEnabled(
				{feature_voice_entrance_sounds: LimitResolver.resolve({key: 'feature_voice_entrance_sounds', fallback: 0})},
				'feature_voice_entrance_sounds',
			),
		[],
	);
	const {t} = useLingui();
	const currentGuildId = SelectedGuildStore.selectedGuildId;
	const guilds = GuildStore.getGuilds();
	const [selectedScopeId, setSelectedScopeId] = useState(() =>
		getEntranceSoundScopeId(currentGuildId ? {kind: 'guild', guildId: currentGuildId} : GLOBAL_ENTRANCE_SOUND_SCOPE),
	);
	const selectedScope = useMemo(
		() => parseEntranceSoundScopeId(selectedScopeId) ?? GLOBAL_ENTRANCE_SOUND_SCOPE,
		[selectedScopeId],
	);
	const orderedGuilds = useMemo(() => {
		const visibleGuilds = [...guilds];
		return visibleGuilds.sort((left, right) => {
			if (left.id === currentGuildId) return -1;
			if (right.id === currentGuildId) return 1;
			return (left.name || '').localeCompare(right.name || '');
		});
	}, [currentGuildId, guilds]);

	const getScopeLabel = useCallback(
		(scope: EntranceSoundScope): string => {
			switch (scope.kind) {
				case 'global':
					return t`All Communities and DMs`;
				case 'guilds':
					return t`All Communities`;
				case 'dms':
					return t`Direct Messages`;
				case 'guild':
					return GuildStore.getGuild(scope.guildId)?.name ?? t`Unknown Community`;
			}
		},
		[t],
	);

	const getScopeDescription = useCallback(
		(scope: EntranceSoundScope): string => {
			switch (scope.kind) {
				case 'global':
					return t`Used by default unless a community or DM sound overrides it.`;
				case 'guilds':
					return t`Used in communities that do not have their own sound.`;
				case 'dms':
					return t`Used for all direct-message calls.`;
				case 'guild':
					return t`Used only in this community.`;
			}
		},
		[t],
	);

	const scopeOptions = useMemo<Array<EntranceSoundScopeOption>>(
		() => [
			{
				value: getEntranceSoundScopeId(GLOBAL_ENTRANCE_SOUND_SCOPE),
				label: getScopeLabel(GLOBAL_ENTRANCE_SOUND_SCOPE),
				description: getScopeDescription(GLOBAL_ENTRANCE_SOUND_SCOPE),
			},
			{
				value: getEntranceSoundScopeId(GUILDS_ENTRANCE_SOUND_SCOPE),
				label: getScopeLabel(GUILDS_ENTRANCE_SOUND_SCOPE),
				description: getScopeDescription(GUILDS_ENTRANCE_SOUND_SCOPE),
			},
			{
				value: getEntranceSoundScopeId(DMS_ENTRANCE_SOUND_SCOPE),
				label: getScopeLabel(DMS_ENTRANCE_SOUND_SCOPE),
				description: getScopeDescription(DMS_ENTRANCE_SOUND_SCOPE),
			},
			...orderedGuilds.map((guild) => ({
				value: getEntranceSoundScopeId({kind: 'guild', guildId: guild.id}),
				label: guild.name || '',
				description: getScopeDescription({kind: 'guild', guildId: guild.id}),
				icon: guild.icon ?? null,
			})),
		],
		[getScopeDescription, getScopeLabel, orderedGuilds],
	);

	const renderScopeRow = useCallback((option: EntranceSoundScopeOption, compact: boolean) => {
		const isGuild = option.value.startsWith('guild:');
		const guildId = isGuild ? option.value.slice('guild:'.length) : null;
		const iconUrl = isGuild && guildId && option.icon ? AvatarUtils.getGuildIconURL({id: guildId, icon: option.icon}) : null;
		const initial = option.label.charAt(0).toUpperCase();

		return (
			<div className={clsx(styles.scopeOptionRow, compact && styles.scopeValueRow)}>
				{isGuild &&
					(iconUrl ? (
						<div className={styles.scopeAvatar} style={{backgroundImage: `url(${iconUrl})`}} aria-hidden />
					) : (
						<div className={styles.scopeAvatarPlaceholder} aria-hidden>
							{initial}
						</div>
					))}
				<div className={styles.scopeText}>
					<span className={styles.scopeLabel}>{option.label}</span>
					{!compact && <span className={styles.scopeDescription}>{option.description}</span>}
				</div>
			</div>
		);
	}, []);

	const renderScopeOption = useCallback(
		(option: EntranceSoundScopeOption) => renderScopeRow(option, false),
		[renderScopeRow],
	);
	const renderScopeValue = useCallback(
		(option: EntranceSoundScopeOption | null) => (option ? renderScopeRow(option, true) : null),
		[renderScopeRow],
	);

	const getScopeHint = useCallback(
		(scope: EntranceSoundScope): string => {
			switch (scope.kind) {
				case 'global':
					return t`Your fallback entrance sound for all communities and direct messages. More specific settings override it.`;
				case 'guilds':
					return t`Used in communities that do not have their own sound set.`;
				case 'dms':
					return t`Used in direct-message calls. If you do not set one, your global sound is used instead.`;
				case 'guild':
					return t`Used in this community. If you do not set one, Fluxer falls back to your community default, then your global default.`;
			}
		},
		[t],
	);

	const {
		entranceSound,
		activeEntranceSound,
		inheritedEntranceSound,
		inheritedFromScope,
		isPreviewing,
		remove,
		preview,
		openUploadDialog,
	} = useEntranceSound(selectedScope);

	const scopeHint = getScopeHint(selectedScope);
	const inheritedFromLabel = inheritedFromScope ? getScopeLabel(inheritedFromScope) : null;
	const hasDirectOverride = entranceSound != null;
	const hasInheritedSound = inheritedEntranceSound != null && inheritedFromLabel != null;
	const uploadButtonLabel = hasDirectOverride
		? t`Replace Entrance Sound`
		: hasInheritedSound
			? t`Set Sound Here`
			: t`Upload Entrance Sound`;

	return (
		<div>
			<div className={styles.label}>
				<Trans>Entrance Sound</Trans>
			</div>
			{!hasVoiceEntranceSounds ? (
				shouldShowPremiumFeatures() ? (
					<div className={styles.premiumCard}>
						<div className={styles.premiumCardHeader}>
							<CrownIcon weight="fill" size={18} className={styles.premiumCardIcon} />
							<span className={styles.premiumCardTitle}>
								<Trans>Custom Entrance Sounds with Plutonium</Trans>
							</span>
						</div>
						<p className={styles.premiumCardDescription}>
							<Trans>
								Upload a custom sound that plays automatically when you join a voice channel. Maximum duration: 5.2
								seconds. Supported formats: MP3, WAV, OGG, M4A, AAC, FLAC, Opus, WebM (max 2MB).
							</Trans>
						</p>
						<Button variant="secondary" small={true} onClick={() => PremiumModalActionCreators.open()}>
							<Trans>Get Plutonium</Trans>
						</Button>
					</div>
				) : (
					<p className={styles.premiumCardDescription}>
						<Trans>Custom entrance sounds are not enabled on this instance.</Trans>
					</p>
				)
			) : (
				<div className={styles.content}>
					<div className={styles.hint}>
						<Trans>
							Choose where your entrance sound should play. More specific settings override broader defaults.
							Maximum duration: 5.2 seconds, 2 MB. Supported formats: MP3, WAV, OGG, M4A, AAC, FLAC, Opus, WebM.
						</Trans>
					</div>

					<div className={styles.scopePicker}>
						<Select<string, false, EntranceSoundScopeOption>
							label={t`Applies To`}
							value={selectedScopeId}
							options={scopeOptions}
							onChange={setSelectedScopeId}
							renderOption={renderScopeOption}
							renderValue={renderScopeValue}
						/>
						<p className={styles.scopeHint}>{scopeHint}</p>
					</div>

					{activeEntranceSound ? (
						<div className={styles.soundCard}>
							<div className={styles.soundCardHeader}>
								<span className={clsx(styles.scopeBadge, hasDirectOverride ? styles.scopeBadgeDirect : styles.scopeBadgeInherited)}>
									{hasDirectOverride ? t`Set here` : t`Inherited`}
								</span>
								{hasInheritedSound && inheritedFromLabel ? (
									<span className={styles.inheritedText}>{t`Inherited from ${inheritedFromLabel}`}</span>
								) : null}
							</div>
							<div className={styles.soundCardContent}>
								<div className={styles.soundCardMain}>
									<Tooltip text={t`Preview sound`}>
										<button
											type="button"
											onClick={preview}
											disabled={isPreviewing}
											className={styles.previewButton}
											aria-label={t`Preview sound`}
										>
											<SpeakerHighIcon size={20} weight="fill" className={styles.previewIcon} />
										</button>
									</Tooltip>
									<div className={styles.soundInfo}>
										<span className={styles.soundFileName}>{activeEntranceSound.fileName}</span>
										<span className={styles.soundDuration}>{activeEntranceSound.duration.toFixed(1)}s</span>
									</div>
								</div>
								{hasDirectOverride && (
									<Tooltip text={t`Remove entrance sound`}>
										<button
											type="button"
											onClick={remove}
											className={styles.deleteButton}
											aria-label={t`Remove entrance sound`}
										>
											<TrashIcon size={16} className={styles.deleteIcon} />
										</button>
									</Tooltip>
								)}
							</div>
						</div>
					) : (
						<div className={styles.emptyState}>
							<div className={styles.emptyStateTitle}>
								<Trans>No entrance sound set here</Trans>
							</div>
							<p className={styles.emptyStateDescription}>
								{selectedScope.kind === 'global' ? (
									<Trans>Upload a default sound to use anywhere you have not set a more specific sound.</Trans>
								) : (
									<Trans>
										This scope does not have its own sound yet, and there is no broader default to use instead.
									</Trans>
								)}
							</p>
						</div>
					)}

					<div className={styles.actions}>
						<Button variant="primary" fitContainer={false} className={styles.actionButton} onClick={openUploadDialog}>
							<div className={styles.uploadButtonContent}>
								<UploadIcon size={16} />
								<span>{uploadButtonLabel}</span>
							</div>
						</Button>
					</div>
				</div>
			)}
		</div>
	);
});
